import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload, Search, Download, Mail, Phone } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CreateContactDialog } from "@/components/crm/CreateContactDialog";
import { ImportContactsDialog } from "@/components/crm/ImportContactsDialog";
import { useToast } from "@/hooks/use-toast";
import { exportCsv } from "@/lib/exports";

interface Contact {
  id: string;
  full_name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  warmth: number;
  last_interaction: string | null;
  organization: string | null;
  family_id?: string;
  contact_types?: string[];
}

const CONTACT_TYPE_LABELS: Record<string, string> = {
  client: "Client",
  prospect: "Prospect",
  vendor: "Vendor",
  fund_manager: "Fund Manager",
  lp: "LP",
  advisor: "Advisor",
  founder: "Founder",
  service_provider: "Service Provider",
};

const getContactTypeColor = (type: string): string => {
  switch (type) {
    case "client": return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "prospect": return "bg-blue-100 text-blue-800 border-blue-200";
    case "vendor": return "bg-orange-100 text-orange-800 border-orange-200";
    case "fund_manager": return "bg-purple-100 text-purple-800 border-purple-200";
    case "lp": return "bg-indigo-100 text-indigo-800 border-indigo-200";
    case "advisor": return "bg-cyan-100 text-cyan-800 border-cyan-200";
    case "founder": return "bg-pink-100 text-pink-800 border-pink-200";
    case "service_provider": return "bg-slate-100 text-slate-800 border-slate-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export default function Contacts() {
  const { isMFO, orgId } = useTenant();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const fetchContacts = async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("*")
        .eq("org_id", orgId)
        .order("last_interaction", { ascending: false, nullsFirst: false });

      if (error) throw error;

      setContacts((data || []) as Contact[]);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) fetchContacts();
  }, [orgId]);

  const handleExport = () => {
    const exportData = contacts.map(c => ({
      full_name: c.full_name,
      email: c.email || "",
      phone: c.phone || "",
      role: c.role || "",
      organization: c.organization || "",
      contact_types: (c.contact_types || []).map(t => CONTACT_TYPE_LABELS[t] || t).join(", "),
      warmth: c.warmth,
      last_interaction: c.last_interaction || "",
    }));

    exportCsv(
      exportData,
      [
        { key: "full_name", label: "Full Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "role", label: "Role" },
        { key: "organization", label: "Organization" },
        { key: "contact_types", label: "Types" },
        { key: "warmth", label: "Warmth" },
        { key: "last_interaction", label: "Last Touch" },
      ],
      "contacts.csv"
    );
  };

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchQuery.toLowerCase();
    const typeMatch = contact.contact_types?.some(type =>
      (CONTACT_TYPE_LABELS[type] || type).toLowerCase().includes(searchLower)
    );
    return contact.full_name.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.role?.toLowerCase().includes(searchLower) ||
      typeMatch;
  });
  
  const getWarmthLabel = (warmth: number) => {
    if (warmth >= 4) return { label: "Hot", color: "bg-success text-success-foreground" };
    if (warmth >= 2) return { label: "Warm", color: "bg-warning text-warning-foreground" };
    return { label: "Cold", color: "bg-muted text-muted-foreground" };
  };
  
  const roleColor = (role: string | null) => {
    if (!role) return "bg-secondary text-secondary-foreground";
    switch (role) {
      case "Principal": return "bg-primary text-primary-foreground";
      case "CIO": return "bg-accent text-accent-foreground";
      case "GP": return "bg-teal text-teal-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          <p className="text-muted-foreground">
            Manage relationships with principals, advisors, and GPs
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
            <Upload className="h-4 w-4" />
            Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Contact
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading contacts...</div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery ? "No contacts found matching your search" : "No contacts yet. Add your first contact to get started."}
        </div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Types</th>
                <th>Role</th>
                <th>Organization</th>
                <th>Contact</th>
                <th>Warmth</th>
                <th>Last Touch</th>
                {isMFO && <th>Family</th>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => {
                const warmth = getWarmthLabel(contact.warmth);
                return (
                  <tr key={contact.id}>
                    <td className="font-medium">{contact.full_name}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {contact.contact_types && contact.contact_types.length > 0 ? (
                          contact.contact_types.map((type) => (
                            <Badge
                              key={type}
                              variant="outline"
                              className={`text-xs ${getContactTypeColor(type)}`}
                            >
                              {CONTACT_TYPE_LABELS[type] || type}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {contact.role && (
                        <Badge variant="outline" className={roleColor(contact.role)}>
                          {contact.role}
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted-foreground">{contact.organization || "—"}</td>
                    <td>
                      <div className="flex flex-col gap-1">
                        {contact.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{contact.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge className={warmth.color}>
                        {warmth.label}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground">
                      {contact.last_interaction ? new Date(contact.last_interaction).toLocaleDateString() : "—"}
                    </td>
                    {isMFO && <td className="text-muted-foreground">—</td>}
                    <td>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateContactDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchContacts}
      />
      
      <ImportContactsDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={fetchContacts}
      />
    </div>
  );
}
